import type { StateCreator } from 'zustand'
import { defaultConnectorRecord, type ConnectorRecord } from '@/types/connector'
import { apiService, ConnectorUpdateRequest } from '@/service/api'
import type { loadingState } from './loadingSlice'
import type { authSessionState } from './authSessionSlice'
import { toast } from 'sonner'
import { i18n } from '@/i18n'

export interface ConnectorState {
  connectors: ConnectorRecord[]
  showConnectorDetailsModal: boolean
  connectorEditData: ConnectorRecord
  // null = not probed yet; the eventhouse type option stays hidden until the
  // flag probe confirms the tenant's /v2/eventhouse surface is open.
  eventhouseAvailable: boolean | null
  // connector ids with an advance POST in flight — the 5s poll must not
  // stack duplicate advances on a slow link.
  provisionAdvanceInFlight: Set<string>
  setConnectorEditData: (data: Partial<ConnectorRecord>) => void
  setConnectorEditConfig: (config: Record<string, unknown>) => void
  deleteConnectorEditConfigKey: (keyToDelete: string) => void
  fetchConnectors: () => Promise<void>
  fetchConnectorById: (connectorId: string) => Promise<ConnectorRecord | undefined>
  createConnector: (connector: ConnectorRecord) => Promise<void>
  updateConnector: (
    connectorId: string,
    updates: ConnectorUpdateRequest
  ) => Promise<void>
  activateConnector: (connectorId: string, active: boolean) => Promise<boolean>
  deleteConnector: (connectorId: string) => Promise<boolean>
  setShowConnectorDetailsModal: (isOpen?: boolean) => void
  probeEventhouseAvailability: () => Promise<void>
  advanceEventhouseProvision: (connectorId: string) => Promise<void>
}


export const connectorSlice: StateCreator<
  ConnectorState & loadingState & authSessionState,
  [],
  [],
  ConnectorState
> = (set, get) => ({
  connectors: [],
  showConnectorDetailsModal: false,
  connectorEditData: defaultConnectorRecord(),
  eventhouseAvailable: null,
  provisionAdvanceInFlight: new Set<string>(),

  /**
   * Sets the data for the connector being edited.
   */
  setConnectorEditData: (data: Partial<ConnectorRecord>) =>
    set((state) => ({
      connectorEditData: { ...state.connectorEditData, ...data },
    })),

  setConnectorEditConfig: (config) =>
    set((state) => ({
      connectorEditData: {
        ...state.connectorEditData,
        config: { ...state.connectorEditData.config, ...config },
      },
    })),

  deleteConnectorEditConfigKey: (keyToDelete: string) =>
    set((state) => {
      const { [keyToDelete]: _, ...newConfig } = state.connectorEditData.config
      return {
        connectorEditData: {
          ...state.connectorEditData,
          config: newConfig,
        },
      }
    }),

  /**
   * Fetches the list of connectors.
   */
  fetchConnectors: async () => {
    const { setLoading } = get()

    try {
      setLoading(true)
      const data = await apiService.getConnectors()
      set({ connectors: data.connectors })
    } catch {
      toast.error(i18n.t('connectors:errors.fetch'))
      set({ connectors: [] })
    } finally {
      setLoading(false)
    }
  },

  /**
   * Fetches a connector by its ID.
   */
  fetchConnectorById: async (connectorId: string) => {
    const { setLoading } = get()
    try {
      setLoading(true)
      const data = await apiService.getConnector(connectorId)
      return data.connector
    }
    catch {
      toast.error(i18n.t('connectors:errors.fetch'))
      return
    }
    finally {
      setLoading(false)
    }
  },

  /**
   * Creates a new connector.
   */
  createConnector: async (connector: ConnectorRecord) => {
    const { setLoading, fetchConnectors } = get()

    try {
      setLoading(true)
      await apiService.createConnector({ connector: connector })
      await fetchConnectors()
      toast.success(i18n.t('connectors:success.create'))
    } catch {
      toast.error(i18n.t('connectors:errors.create'))
    } finally {
      setLoading(false)
    }
  },

  /**
   * Updates an existing connector.
   */
  updateConnector: async (
    connectorId: string,
    updates: ConnectorUpdateRequest
  ) => {
    const { setLoading, fetchConnectors } = get()

    try {
      setLoading(true)
      await apiService.updateConnector(connectorId, updates)
      await fetchConnectors()
      toast.success(i18n.t('connectors:success.update'))
    } catch {
      toast.error(i18n.t('connectors:errors.update'))
    } finally {
      setLoading(false)
    }
  },

  /**
   * Activates or deactivates a connector.
   */
  activateConnector: async (connectorId: string, active: boolean) => {
    const { setLoading, fetchConnectors } = get()

    try {
      setLoading(true)
      await apiService.activateConnector(connectorId, {
        activate: active,
      })
      await fetchConnectors()
      return true
    } catch {
      toast.error(i18n.t('connectors:errors.activate'))
      return false
    } finally {
      setLoading(false)
    }
  },

  /**
   * Deletes a connector.
   */
  deleteConnector: async (connectorId: string) => {
    const { setLoading, fetchConnectors } = get()

    try {
      setLoading(true)
      await apiService.deleteConnector(connectorId)
      await fetchConnectors()
      toast.success(i18n.t('connectors:success.delete'))
      return true
    } catch {
      toast.error(i18n.t('connectors:errors.delete'))
      return false
    } finally {
      setLoading(false)
    }
  },

  /**
   * Controls the visibility of the connector details modal.
   */
  setShowConnectorDetailsModal: (isOpen: boolean = true) =>
    set({ showConnectorDetailsModal: isOpen }),

  /**
   * One-shot probe of the eventhouse self-service lane (flag-gated
   * server-side). A definitive answer is cached for the session; a
   * transient failure leaves `null` so the next call re-probes.
   */
  probeEventhouseAvailability: async () => {
    if (get().eventhouseAvailable !== null) return
    try {
      const available = await apiService.isEventhouseAvailable()
      set({ eventhouseAvailable: available })
    } catch {
      // Transient (network/5xx) — do not cache as unavailable.
    }
  },

  /**
   * Advance an eventhouse connector's provisioning (auto-fired when the
   * poll sees 'seeded', and by the Retry button on 'error'). Deduped per
   * connector so overlapping polls can't stack advances.
   */
  advanceEventhouseProvision: async (connectorId: string) => {
    const { provisionAdvanceInFlight, fetchConnectors } = get()
    if (provisionAdvanceInFlight.has(connectorId)) return
    set({
      provisionAdvanceInFlight: new Set(provisionAdvanceInFlight).add(connectorId),
    })
    try {
      await apiService.advanceConnectorProvision(connectorId)
      await fetchConnectors()
    } catch {
      toast.error(i18n.t('connectors:errors.provision'))
      // Refresh anyway — the backend stamps provision_error for the row.
      await fetchConnectors()
    } finally {
      set((state) => {
        const next = new Set(state.provisionAdvanceInFlight)
        next.delete(connectorId)
        return { provisionAdvanceInFlight: next }
      })
    }
  },
})
